import {
  ActionMenu,
  PreviewNote,
  RInputModal,
  RModal,
  TagPrivacy,
  TagTopic,
  TopicStates,
  UserAvatar,
} from '@/components'
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Topic } from '@/types'
import { PrivacyType, PrivacyTypes, ResourceType, TopicType, TopicTypes } from '@/utils/constants'
import { DeleteOutline, EditOutlined } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import {
  useDeleteTopic,
  useGetPermissions,
  useUpdatePatternTopic,
  useUpdateVisibility,
} from '@/hooks/api/dashboard'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useAuth, useBrowserQuery, useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import { getTopicNameFromTopic, getTopicTypeLabel, pluralize } from '@/utils/helper'
import { RBox } from '@/pages/__styled'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { emitAppEvent } from '@/events/eventBus'
import { AppEvents } from '@/events/eventTypes'

interface HeaderProps {
  topic: Topic
  showBackButton?: boolean
  onClickViewInteractions: () => void
  onClickBack: () => void
  onClickComment: () => void
  onClickPrivacy: () => void
  onDeletedTopic?: () => void
}

export default function TopicDetailsHeader({
  topic: incomingTopic,
  onClickViewInteractions,
  onClickBack,
  onDeletedTopic,
  showBackButton = true,
}: HeaderProps) {
  const [modalOpened, setModalOpened] = useState(false)
  const updateTopic = useUpdatePatternTopic()
  const updateVisibility = useUpdateVisibility()
  const getPermissions = useGetPermissions()
  const theme = useTheme()
  const [renameModalOpened, setRenameModalOpened] = useState(false)

  const lessThanSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const topicData = TopicTypes[Number(incomingTopic.topicType)]
  const privacyData = PrivacyTypes[Number(incomingTopic.visibility)]

  const { user } = useAuth()
  const [ewUser] = useLocalStorage('ewUser', null)
  const deleteTopic = useDeleteTopic(incomingTopic.topicID)
  const navigate = useNavigate()
  const { removeTopic } = useAppStore()
  const query = useBrowserQuery()
  const topicID = query.get('topicID') || ''

  const [topic, setTopic] = useState(incomingTopic)
  const isMyTopic = user?.userID == topic.userID

  useEffect(() => {
    setTopic(incomingTopic)
  }, [incomingTopic])

  useEffect(() => {
    if (deleteTopic.isSuccess) {
      removeTopic(topicID)
      onDeletedTopic && onDeletedTopic()
    }
  }, [deleteTopic.isSuccess])

  if (!topicData || !privacyData) return null

  const onCloseModal = () => {
    setModalOpened(false)
  }

  const onDeleteTopic = () => {
    toast.promise(deleteTopic.mutateAsync(), {
      loading: 'Deleting...',
      success: <Typography variant='p'>Successfully deleted</Typography>,
      error: <Typography variant='p'>Failed to delete</Typography>,
    })
  }

  const topicActions = useMemo(() => {
    let items = [
      {
        icon: <EditOutlined />,
        name: 'Rename',
        onClick: () => setRenameModalOpened(true),
      },
    ]

    if (topic.visibility == PrivacyType.PRIVATE.toString()) {
      items.push({
        icon: <SVG src={SVGS.FilterPrivacyPublic} />,
        name: 'Save as Public',
        onClick: () => changeVisibility(PrivacyType.PUBLIC),
      })
    } else {
      items.push({
        icon: <SVG src={SVGS.FilterPrivacyPrivate} />,
        name: 'Make Private',
        onClick: () => changeVisibility(PrivacyType.PRIVATE),
      })
    }
    items.push({
      icon: <DeleteOutline />,
      name: 'Delete',
      onClick: () => setModalOpened(true),
    })

    return items
  }, [topic])

  const changeVisibility = (newPrivacy: PrivacyType) => {
    if (topic != null) {
      // Get Permission ID

      const updatePerm = (permID: string) => {
        updateVisibility
          .mutateAsync({
            resourceID: topic.topicID,
            cliqueTypes: topic.cliqueTypes,
            cliques: [],
            connections: [],
            accessTypeID: newPrivacy.toString(),
            permissionID: permID,
            resourceType: ResourceType.TOPIC,
          })
          .then((value) => {
            toast.remove('perm-load')
            toast.success('Updated')

            emitAppEvent(AppEvents.TOPIC_PRIVACY_UPDATED, {
              topic: { ...topic, visibility: newPrivacy.toString() },
            })

            setTopic({
              ...topic,
              visibility: newPrivacy.toString(),
            })
          })
          .catch((err) => {
            toast.remove('perm-load')
            toast.error(err.toString())
          })
      }

      toast.loading('Updating...', { id: 'perm-load' })
      getPermissions
        .mutateAsync(topic.topicID)
        .then((permData) => {
          const { permissionID } = permData
          updatePerm(permissionID)
        })
        .catch((err) => {
          toast.remove('perm-load')
        })
    }
  }

  const onConfirmRenameTopic = (text: string) => {
    if (topic) {
      toast.loading('Updating...', { id: 'update-topic' })
      updateTopic
        .mutateAsync({ topic, topicName: text })
        .then((value) => {
          toast.remove('update-topic')
          toast.success('Updated Successfully.')

          const noteText = {
            text: `<p>${text}</p>`,
            attachments: [],
          }

          let updatedTopic = {
            ...topic,
            topicName: text,
            firstNote: {
              ...topic.firstNote,
              noteText: JSON.stringify(noteText),
            },
          }

          emitAppEvent(AppEvents.TOPIC_UPDATED, { topic: updatedTopic })
          setTopic((prev) => ({ ...updatedTopic }))
        })
        .catch((e) => {
          toast.remove('update-topic')
          toast.error(e.toString())
        })
    }
  }

  const noteCount = useMemo(() => {
    const count = Number(topic.noteCount || topic.pagination?.notesCount)
    return count > 0 ? count - 1 : count
  }, [topic])

  const totalInteractions = useMemo(() => {
    return Number(topic.totalLikes) + Number(topic.totalComments)
  }, [topic])

  const onClickAvatar = () => {
    if (!ewUser) return
    if (isMyTopic) {
      navigate(`/profile/me`)
    } else {
      let previewQuery = new URLSearchParams()
      previewQuery.set('id', topic.userID)
      navigate(`/profile/user?${previewQuery.toString()}`)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // alignItems: { xs: 'stretch', sm: 'stretch' },
          justifyContent: 'space-between',
          gap: '16px',
          padding: '16px',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        {/* Back button + title + badges */}
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={{ xs: '12px', sm: '20px' }}
          alignItems={'center'}
          minWidth={0}
        >
          {showBackButton && (
            <IconButton
              onClick={onClickBack}
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                flexShrink: 0,
                backgroundColor: 'rgba(248, 250, 252, 0.8)',
                border: '1px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '12px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  transform: 'translateX(-2px)',
                  borderColor: '#6366f1',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                },
                '&:active': {
                  transform: 'translateX(0)',
                },
              }}
            >
              <SVG src={SVGS.BackArrow} color='#475569' width={18} height={18} />
            </IconButton>
          )}
          <RBox justifyContent={'space-between'} flex={1}>
            <Typography
              fontSize={{ xs: '17px', sm: '22px' }}
              fontWeight={'700'}
              color='#0f172a'
              sx={{
                letterSpacing: '-0.02em',
                wordBreak: 'break-word',
              }}
            >{`${getTopicNameFromTopic(topic)}`}</Typography>
            {isMyTopic && (
              <IconButton
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                  border: '1px solid rgba(226, 232, 240, 0.6)',
                  borderRadius: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    borderColor: '#cbd5e1',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <ActionMenu
                  id='topic-item-menu'
                  parent={<SVG src={SVGS.MoreIcon} color='#64748b' width={18} height={18} />}
                  actions={topicActions}
                />
              </IconButton>
            )}
          </RBox>
        </Box>

        {/* Actions: visibility toggle + menu, or avatar + username */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', minWidth: 0 }}>
            <Box
              onClick={onClickAvatar}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <UserAvatar
                borderRadius={100}
                name={topic.userName}
                url={topic.profilePicThumbURL || topic.profileThumbURL}
                size={40}
              />
            </Box>
            <Box sx={{ alignItems: 'center', minWidth: 0 }}>
              <Box onClick={onClickAvatar}>
                <Typography
                  fontSize={'14px'}
                  fontWeight={600}
                  color='#0f172a'
                  noWrap
                  sx={{
                    letterSpacing: '-0.01em',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: '#6366f1',
                    },
                  }}
                >
                  {isMyTopic ? 'You' : topic.userName}
                </Typography>
              </Box>
              <Typography
                fontSize={'12px'}
                fontWeight={500}
                sx={{ color: '#64748b', letterSpacing: '-0.01em' }}
                whiteSpace={'nowrap'}
              >
                {`Added `}
                <CustomTimeAgo date={topic.dateAdded} />
              </Typography>
            </Box>
          </Box>

          {isMyTopic && (
            <>
              {topic.visibility == PrivacyType.PRIVATE.toString() ? (
                <Button
                  variant='contained'
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    padding: { xs: '8px 14px', sm: '10px 20px' },
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: { xs: '13px', sm: '14px' },
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    flex: { xs: 1, sm: 'initial' },
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow:
                        '0 8px 20px rgba(99, 102, 241, 0.35), 0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => {
                    changeVisibility(PrivacyType.PUBLIC)
                  }}
                  startIcon={<SVG src={SVGS.FilterPrivacyPublic} width={16} height={16} />}
                >
                  {lessThanSmall ? 'Public' : 'Make Public'}
                </Button>
              ) : (
                <Button
                  variant='contained'
                  sx={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    color: 'white',
                    padding: { xs: '8px 14px', sm: '10px 20px' },
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: { xs: '13px', sm: '14px' },
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    flex: { xs: 1, sm: 'initial' },
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.35), 0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => {
                    changeVisibility(PrivacyType.PRIVATE)
                  }}
                  startIcon={<SVG src={SVGS.FilterPrivacyPrivate} width={16} height={16} />}
                >
                  {lessThanSmall ? 'Private' : 'Make Private'}
                </Button>
              )}
            </>
          )}
        </Box>
        <RBox sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              rowGap: '8px',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '8px',
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.15)',
              }}
            >
              <Typography
                fontSize={'12px'}
                fontWeight={600}
                sx={{ color: '#6366f1', letterSpacing: '-0.01em' }}
                whiteSpace={'nowrap'}
              >
                {pluralize('', noteCount || 0, 'Photo', 'Photos')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '8px',
                backgroundColor: `${TopicTypes[Number(topic.topicType)].color}14`,
                border: `1px solid ${TopicTypes[Number(topic.topicType)].color}26`,
              }}
            >
              <Typography
                fontSize={'12px'}
                fontWeight={600}
                sx={{
                  color: TopicTypes[Number(topic.topicType)].color,
                  letterSpacing: '-0.01em',
                }}
                whiteSpace={'nowrap'}
              >
                {getTopicTypeLabel(topic.topicType)}
              </Typography>
            </Box>
          </Box>
        </RBox>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          // padding: { xs: '12px 16px', sm: '20px 32px' },
          padding: '20px 16px',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          overflowX: 'auto',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          backgroundColor: '#fafafa',
        }}
      >
        <Box flex={1} display='flex' alignItems={'center'} gap={'16px'} minWidth={0}>
          <TopicStates topic={topic} />
        </Box>
      </Box>

      <RModal
        open={modalOpened}
        onClose={onCloseModal}
        title={`Delete ${topicData.title}`}
        description={`Are you sure you want to delete this ${topicData.title.toLowerCase()}?`}
        submitText='Delete'
        onSubmit={onDeleteTopic}
      />
      <RInputModal
        open={renameModalOpened}
        maxLength={30}
        onClose={() => setRenameModalOpened(false)}
        title={`Change ${topicData.title} Name`}
        text={getTopicNameFromTopic(topic)}
        placeholder={`Enter ${topicData.title} Description`}
        description=''
        submitText='Save'
        onSubmit={onConfirmRenameTopic}
      />
    </Box>
  )
}
